import { useToast } from '@chakra-ui/react'
import { createContext, FC, useState } from 'react'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import followUpFornecedorService from '@service/followUpFornecedor'

import {
  IConfigContext,
  ICreateConfigPayload,
  IEditConfigPayload,
  IFollowUpConfig,
  IModalEditConfig
} from '@base/@types/providers/FollowUpFornecedor/ConfigProvider'

export const ConfigContext = createContext<IConfigContext>({} as IConfigContext)

const ConfigProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [data, updateData] = useImmer<IFollowUpConfig[]>([])
  const [modalCreate, setModalCreate] = useState(false)
  const [modalEdit, setModalEdit] = useState<IModalEditConfig | null>(null)

  const getData = async () => {
    setLoading(true)
    try {
      const { data: res } = await followUpFornecedorService.getConfigs()
      updateData(res.data ?? [])
    } catch {
      toast({
        title: 'Erro ao carregar configurações',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async (payload: ICreateConfigPayload) => {
    try {
      await followUpFornecedorService.createConfig(payload)
      toast({
        title: 'Configuração criada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
      setModalCreate(false)
      await getData()
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Erro ao criar configuração'
      toast({
        title: msg,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    }
  }

  const handleUpdate = async (payload: IEditConfigPayload) => {
    if (!modalEdit) return
    try {
      await followUpFornecedorService.updateConfig(modalEdit.grupoId, payload)
      toast({
        title: 'Configuração atualizada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
      setModalEdit(null)
      await getData()
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? 'Erro ao atualizar configuração'
      toast({
        title: msg,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    }
  }

  const handleDelete = async (grupoId: string) => {
    const result = await Swal.fire({
      title: 'Remover configuração?',
      text: 'Todos os disparos deste grupo serão removidos.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1d5840',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar'
    })
    if (!result.isConfirmed) return
    try {
      await followUpFornecedorService.deleteConfig(grupoId)
      toast({
        title: 'Configuração removida',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
      await getData()
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Erro ao remover configuração'
      toast({
        title: msg,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    }
  }

  const handleOpenEdit = (item: IFollowUpConfig) => {
    // Group by grupoId to collect all suppliers and stores for this group
    const group = data.filter(d => d.grupoId === item.grupoId && d.isActive)
    const supplierIds = [...new Set(group.map(d => Number(d.supplierId)))]
    const storeIds = [...new Set(group.map(d => Number(d.storeId)))]
    const emails = [...new Set(group.map(d => d.email).filter(Boolean))]
    setModalEdit({ grupoId: item.grupoId, emails, supplierIds, storeIds })
  }

  return (
    <ConfigContext.Provider
      value={{
        loading,
        data,
        modalCreate,
        modalEdit,
        getData,
        handleOpenCreate: () => setModalCreate(true),
        handleCloseCreate: () => setModalCreate(false),
        handleOpenEdit,
        handleCloseEdit: () => setModalEdit(null),
        handleCreate,
        handleUpdate,
        handleDelete
      }}
    >
      {children}
    </ConfigContext.Provider>
  )
}

export default ConfigProvider
